import { ChevronDown } from 'lucide-react';
import { FC, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopoverDismiss } from '../usePopoverDismiss';
import styles from './PunchupDropdown.module.css';

export interface DropdownOption {
	value: string | number;
	label: string;
}

export interface PunchupDropdownProps {
	options: DropdownOption[];
	value: string | number | null;
	onChange: (value: string | number) => void;
	placeholder?: string;
	disabled?: boolean;
	error?: string;
}

/**
 * Non-searchable custom dropdown for any standalone Punchup context.
 * 48px height (form-field height) — compose punchupDropdownControlsHeight from global to override to 40px.
 * Does not require a FormProvider. Panel portals to document.body so it is never clipped
 * by ancestor overflow containers (e.g. PunchupDialogue).
 */
export const PunchupDropdown: FC<PunchupDropdownProps> = ({
	options,
	value,
	onChange,
	placeholder = 'Select…',
	disabled = false,
	error,
}) => {
	const [open, setOpen] = useState(false);
	const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
	const wrapperRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	const selectedOption = options.find((o) => String(o.value) === String(value)) ?? null;

	const updatePanelPosition = () => {
		if (!triggerRef.current) return;
		const rect = triggerRef.current.getBoundingClientRect();
		setPanelStyle({ top: rect.bottom + 4, left: rect.left, width: rect.width });
	};

	// Outside click dismisses; scroll/resize RE-ANCHORS the panel rather than closing it — closing on
	// those events dismissed the dropdown mid-interaction inside the height-synced iframe. See usePopoverDismiss.
	usePopoverDismiss({
		open,
		wrapperRef,
		panelRef,
		onDismiss: () => setOpen(false),
		onReposition: updatePanelPosition,
	});

	const handleToggle = () => {
		if (!open) updatePanelPosition();
		setOpen((v) => !v);
	};

	const handleSelect = (option: DropdownOption) => {
		onChange(option.value);
		setOpen(false);
	};

	const panel = open
		? ReactDOM.createPortal(
				<div ref={panelRef} className={styles.panel} style={panelStyle}>
					<div className={styles.list}>
						{options.map((option) => {
							const isSelected = String(option.value) === String(value);
							return (
								<button
									key={option.value}
									type="button"
									className={`${styles.option} ${isSelected ? styles.optionSelected : ''}`}
									onMouseDown={() => handleSelect(option)}
								>
									{option.label}
									{isSelected && (
										<span className={styles.optionCheckBox} aria-hidden="true">
											<svg className={styles.optionCheckSvg} viewBox="0 0 10 8" fill="none" aria-hidden="true">
												<path
													d="M1 4L3.5 6.5L9 1"
													stroke="currentColor"
													strokeWidth="1.75"
													strokeLinecap="round"
													strokeLinejoin="round"
												/>
											</svg>
										</span>
									)}
								</button>
							);
						})}
					</div>
				</div>,
				document.body
		  )
		: null;

	return (
		<div className={styles.wrapper} ref={wrapperRef}>
			<button
				ref={triggerRef}
				type="button"
				className={[styles.trigger, open ? styles.triggerOpen : null, error ? styles.triggerError : null]
					.filter(Boolean)
					.join(' ')}
				onClick={handleToggle}
				disabled={disabled}
			>
				<span className={selectedOption ? undefined : styles.placeholder}>{selectedOption?.label ?? placeholder}</span>
				<ChevronDown size={16} className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} />
			</button>
			{panel}
			{error && <p className={styles.errorText}>{error}</p>}
		</div>
	);
};
